import type {
  FilterFieldConfig,
  DatasetSearchParams,
  DatasetFacets,
} from '../types';
import { labelForFacetValue } from './facet-label';
import { readValueHistogram, valueRangeLabel } from './value-histogram';

// A caller-agnostic, normalized view of one active filter value. The sidebar
// display and the modal preview each format these into their own chip shape, so
// the field-type enumeration lives here once instead of in every builder.
export type FieldChip =
  // A ready-to-show label removed by clearing a single param
  // (text / radio / checkbox / combo text part).
  | { kind: 'labeled'; key: string; param: string; label: string }
  // One picklist value; callers decide whether to prefix it with the field
  // label. `rawValue` is spliced from the array, `resolved` is the display name.
  | {
      kind: 'value';
      key: string;
      param: string;
      rawValue: string;
      resolved: string;
      fieldLabel: string;
    }
  // A min/max range; callers decide split-vs-combined and how to format bounds.
  | {
      kind: 'range';
      fieldLabel: string;
      minParam: string;
      maxParam: string;
      min?: string;
      max?: string;
      format: 'date' | 'numeric';
    }
  // A value range shown as one chip ('1,500 – 3,000 ft'), cleared as a pair.
  | {
      kind: 'value-range';
      key: string;
      fieldLabel: string;
      minParam: string;
      maxParam: string;
      label: string;
    };

interface FieldChips {
  category: string;
  chips: FieldChip[];
}

// The param keys a field owns, in the order the sidebar clears them.
export const fieldParamKeys = (field: FilterFieldConfig): string[] => {
  switch (field.type) {
    case 'date-range':
    case 'numeric-range':
    case 'value-histogram':
      return [field.minParam, field.maxParam];
    case 'multi-select-text-search-combo':
      return [field.param, field.textParam];
    case 'tree-table':
      return [field.param, field.childParam];
    default:
      return [field.param];
  }
};

// Normalize a single field's active values into caller-agnostic chips. Accepts
// a partial payload so both the committed search and the staged pending set fit.
// Push one `value` chip per selected value (shared by the multi-select family
// and the array half of the text-search combo).
const pushValueChips = (
  chips: FieldChip[],
  field: { param: string; facetKey: string; label: string },
  values: string[],
  facets: DatasetFacets | null | undefined
) => {
  for (const val of values) {
    chips.push({
      kind: 'value',
      key: `${field.param}:${val}`,
      param: field.param,
      rawValue: val,
      resolved: labelForFacetValue(facets, field.facetKey, val),
      fieldLabel: field.label,
    });
  }
};

export const fieldToChips = (
  field: FilterFieldConfig,
  params: Partial<DatasetSearchParams>,
  facets: DatasetFacets | null | undefined
): FieldChips => {
  const chips: FieldChip[] = [];

  switch (field.type) {
    case 'text':
    case 'survey-area': {
      const val = params[field.param] as string | undefined;
      if (val) {
        chips.push({
          kind: 'labeled',
          key: field.param,
          param: field.param,
          label: `${field.label}: ${val}`,
        });
      }
      break;
    }
    case 'radio': {
      const val = params[field.param] as string | undefined;
      if (val) {
        chips.push({
          kind: 'labeled',
          key: field.param,
          param: field.param,
          label: `${field.label}: ${labelForFacetValue(
            facets,
            field.facetKey,
            val
          )}`,
        });
      }
      break;
    }
    case 'checkbox': {
      if (params[field.param] === 'true') {
        chips.push({
          kind: 'labeled',
          key: field.param,
          param: field.param,
          label: field.label,
        });
      }
      break;
    }
    case 'date-range':
    case 'numeric-range': {
      const min = params[field.minParam] as string | undefined;
      const max = params[field.maxParam] as string | undefined;
      if (min || max) {
        chips.push({
          kind: 'range',
          fieldLabel: field.label,
          minParam: field.minParam,
          maxParam: field.maxParam,
          min,
          max,
          format: field.type === 'date-range' ? 'date' : 'numeric',
        });
      }
      break;
    }
    case 'value-histogram': {
      const label = valueRangeLabel(
        params[field.minParam],
        params[field.maxParam],
        readValueHistogram(facets, field.facetKey)?.units
      );
      if (label) {
        chips.push({
          kind: 'value-range',
          key: `${field.minParam}-${field.maxParam}`,
          fieldLabel: field.label,
          minParam: field.minParam,
          maxParam: field.maxParam,
          label,
        });
      }
      break;
    }
    case 'picklist':
    case 'multi-select':
    case 'multi-select-list': {
      pushValueChips(
        chips,
        field,
        (params[field.param] as string[] | undefined) ?? [],
        facets
      );
      break;
    }
    case 'tree-table': {
      // Parent (state) then child (county) selections, each resolved through
      // its own facet. Both clear via the tree's row checkboxes / group trash.
      const parents = (params[field.param] as string[] | undefined) ?? [];
      for (const val of parents) {
        chips.push({
          kind: 'value',
          key: `${field.param}:${val}`,
          param: field.param,
          rawValue: val,
          resolved: labelForFacetValue(facets, field.facetKey, val),
          fieldLabel: field.label,
        });
      }
      const children = (params[field.childParam] as string[] | undefined) ?? [];
      for (const val of children) {
        chips.push({
          kind: 'value',
          key: `${field.childParam}:${val}`,
          param: field.childParam,
          rawValue: val,
          resolved: labelForFacetValue(facets, field.childFacetKey, val),
          fieldLabel: field.label,
        });
      }
      break;
    }
    case 'multi-select-text-search-combo': {
      pushValueChips(
        chips,
        field,
        (params[field.param] as string[] | undefined) ?? [],
        facets
      );
      const textVal = params[field.textParam] as string | undefined;
      if (textVal) {
        chips.push({
          kind: 'labeled',
          key: field.textParam,
          param: field.textParam,
          label: `${field.label}: ${textVal}`,
        });
      }
      break;
    }
  }

  return { category: field.category, chips };
};
